import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { API_ENDPOINTS } from '@nimbazaar/constants';
import type { RootState } from '../store';
import { authActions } from '../slices/authSlice';

const API_URL = import.meta.env?.VITE_API_URL || 'http://localhost:3000/api';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.accessToken;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    headers.set('Content-Type', 'application/json');
    return headers;
  },
});

let refreshPromise: Promise<void> | null = null;

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  if (result.error?.status === 401) {
    // If a refresh is already in-flight, wait for it then retry
    if (refreshPromise) {
      try {
        await refreshPromise;
        result = await rawBaseQuery(args, api, extraOptions);
      } catch {
        // Refresh failed — already handled by the original caller
      }
      return result;
    }

    const refreshToken = (api.getState() as RootState).auth.refreshToken;
    if (!refreshToken) {
      api.dispatch(authActions.logout());
      return result;
    }

    refreshPromise = (async () => {
      try {
        const refreshResult = await rawBaseQuery(
          {
            url: API_ENDPOINTS.AUTH.REFRESH,
            method: 'POST',
            body: { refreshToken },
          },
          api,
          extraOptions
        );

        if (refreshResult.data) {
          const data = (refreshResult.data as { data: { accessToken: string; refreshToken: string } }).data;
          api.dispatch(authActions.setTokens({
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
          }));
        } else {
          api.dispatch(authActions.logout());
          throw new Error('Refresh failed');
        }
      } finally {
        refreshPromise = null;
      }
    })();

    try {
      await refreshPromise;
      result = await rawBaseQuery(args, api, extraOptions);
    } catch {
      // refresh failed, result still has the 401
    }
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['User', 'Product', 'Products', 'Order', 'Orders', 'Cart', 'Categories', 'Reviews', 'Ad', 'Ads', 'Bids', 'Comments', 'Conversations', 'Messages', 'UnreadCount', 'Transactions', 'Cities', 'CreditConfig'],
  endpoints: () => ({}),
});
